import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Loader2,
  SquarePen,
  MoreHorizontal,
  PanelLeft,
  PanelRight,
  Pencil,
  Trash2,
} from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '@/utils/cn';
import { type WebConversationListItem } from '../../lib/chat-api';
import type { AuthUser, WebsiteCredits } from '../../lib/auth';
import { groupConversationsByDate } from './chat-utils';
import { chatLucideIconProps } from './chat-ui';
import { ChatSidebarAccount } from './chat-sidebar-account';
import { ChatSidebarNavItem, ChatSidebarSearchEntry } from './chat-sidebar-nav';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

function normalizedConversationId(conversation: WebConversationListItem) {
  const conversationId = conversation.conversationId?.trim() ?? '';
  return conversationId.length > 0 ? conversationId : null;
}

function ChatSidebarConversationItem({
  conversation,
  isActive,
  isDeleting,
  isRenaming,
  onSelect,
  onRename,
  onDelete,
}: {
  conversation: WebConversationListItem;
  isActive: boolean;
  isDeleting: boolean;
  isRenaming: boolean;
  onSelect: (conversationId: string) => void;
  onRename: (conversationId: string) => void;
  onDelete: (conversationId: string) => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const isMutating = isDeleting || isRenaming;
  const conversationId = normalizedConversationId(conversation);
  const canMutateConversation = conversationId !== null;

  return (
    <div className="group relative">
      <button
        type="button"
        onClick={() => {
          if (conversationId) {
            onSelect(conversationId);
          }
        }}
        disabled={!conversationId}
        className={cn(
          'h-7 w-full rounded-[5px] px-[14px] py-[2px] text-left transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-ring/20',
          'text-foreground',
          isActive
            ? 'bg-black/4 dark:bg-white/4'
            : 'group-hover:bg-black/6 dark:group-hover:bg-white/6'
        )}
      >
        <span className="flex h-full min-w-0 items-center gap-1 rounded-lg py-1 pl-1.5 pr-2.5">
          <span className="chat-type-body-sm-strong flex h-4 min-w-0 flex-1 flex-col justify-center truncate text-[#000000D9] dark:text-white/85">
            {conversation.title || 'Untitled conversation'}
          </span>
        </span>
      </button>

      <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className={cn(
              'absolute top-1/2 right-[14px] flex size-[18px] -translate-y-1/2 items-center justify-center rounded-[3.6px] text-muted-foreground outline-none transition-opacity duration-150 hover:bg-muted/60 focus-visible:ring-2 focus-visible:ring-ring/20 disabled:pointer-events-none disabled:opacity-50',
              menuOpen || isMutating
                ? 'pointer-events-auto opacity-100'
                : 'pointer-events-none opacity-0 group-hover:pointer-events-auto group-hover:opacity-100 group-focus-within:pointer-events-auto group-focus-within:opacity-100 focus-visible:pointer-events-auto focus-visible:opacity-100'
            )}
            aria-label={`More actions for ${conversation.title || 'conversation'}`}
            disabled={isMutating}
            onClick={event => {
              event.stopPropagation();
            }}
          >
            {isMutating ? (
              <Loader2
                {...chatLucideIconProps}
                className="size-[14px] animate-spin"
              />
            ) : (
              <MoreHorizontal
                {...chatLucideIconProps}
                className="size-[14px]"
              />
            )}
          </button>
        </DropdownMenuTrigger>

        <DropdownMenuContent
          align="start"
          side="right"
          sideOffset={10}
          alignOffset={-4}
        >
          <DropdownMenuItem
            disabled={isMutating || !canMutateConversation}
            onSelect={event => {
              event.preventDefault();
              if (!conversationId) {
                return;
              }
              setMenuOpen(false);
              onRename(conversationId);
            }}
          >
            <Pencil {...chatLucideIconProps} className="size-4" />
            Rename
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            variant="destructive"
            disabled={isMutating || !canMutateConversation}
            onSelect={event => {
              event.preventDefault();
              if (!conversationId) {
                return;
              }
              setMenuOpen(false);
              onDelete(conversationId);
            }}
          >
            <Trash2 {...chatLucideIconProps} className="size-4" />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

export function ChatSidebar({
  conversations,
  activeConversationId,
  loading,
  hasMoreConversations,
  loadingMoreConversations,
  creatingConversation = false,
  autoLoadMoreThreshold = 160,
  enableAutoLoadMore = true,
  className,
  isSidebarOpen = true,
  isSearchOpen = false,
  onSelect,
  onCreate,
  onSearchOpen,
  onLoadMoreConversations,
  onToggleSidebar,
  onRename,
  onDelete,
  deletingConversationId,
  renamingConversationId,
  currentUser,
  credits,
  loadingCredits,
  onLogout,
  onOpenBilling,
  onOpenRedeem,
  onOpenComputers,
  onOpenBook,
  onOpenIntegrations,
  onDownloadClient,
}: {
  conversations: WebConversationListItem[];
  activeConversationId?: string;
  loading: boolean;
  hasMoreConversations: boolean;
  loadingMoreConversations: boolean;
  creatingConversation?: boolean;
  autoLoadMoreThreshold?: number;
  enableAutoLoadMore?: boolean;
  className?: string;
  isSidebarOpen?: boolean;
  isSearchOpen?: boolean;
  onSelect: (conversationId: string) => void;
  onCreate: () => void;
  onSearchOpen: () => void;
  onLoadMoreConversations: () => void;
  onToggleSidebar?: () => void;
  onRename: (conversationId: string) => void;
  onDelete: (conversationId: string) => void;
  deletingConversationId?: string | null;
  renamingConversationId?: string | null;
  currentUser?: AuthUser | null;
  credits?: WebsiteCredits | null;
  loadingCredits: boolean;
  onLogout: () => Promise<void>;
  onOpenBilling: () => void;
  onOpenRedeem: () => void;
  onOpenComputers: () => void;
  onOpenBook: () => void;
  onOpenIntegrations: () => void;
  onDownloadClient: () => void;
}) {
  const conversationScrollAreaRef = useRef<HTMLDivElement | null>(null);
  const conversationLoadMoreSentinelRef = useRef<HTMLDivElement | null>(null);
  const conversationGroups = groupConversationsByDate(
    conversations.filter(conversation => normalizedConversationId(conversation))
  );
  const newChatAction = (
    <ChatSidebarNavItem
      icon={
        creatingConversation ? (
          <Loader2 {...chatLucideIconProps} className="size-4 animate-spin" />
        ) : (
          <SquarePen {...chatLucideIconProps} className="size-4" />
        )
      }
      label="New Chat"
      onClick={onCreate}
      disabled={creatingConversation}
    />
  );
  const sidebarToggleAction = onToggleSidebar ? (
    <Button
      variant="ghost"
      size="icon-sm"
      className="size-8 shrink-0 rounded-md text-muted-foreground shadow-none hover:bg-black/[0.04] hover:text-foreground dark:hover:bg-white/[0.04]"
      onClick={onToggleSidebar}
      aria-label={isSidebarOpen ? 'Collapse chats' : 'Expand chats'}
    >
      {isSidebarOpen ? (
        <PanelRight {...chatLucideIconProps} className="size-4" />
      ) : (
        <PanelLeft {...chatLucideIconProps} className="size-4" />
      )}
    </Button>
  ) : null;

  useEffect(() => {
    const scrollArea = conversationScrollAreaRef.current;
    const sentinel = conversationLoadMoreSentinelRef.current;
    if (
      !scrollArea ||
      !sentinel ||
      !enableAutoLoadMore ||
      loading ||
      loadingMoreConversations ||
      !hasMoreConversations
    ) {
      return;
    }

    const observer = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          if (!entry.isIntersecting) {
            continue;
          }

          onLoadMoreConversations();
          break;
        }
      },
      {
        root: scrollArea,
        rootMargin: `0px 0px ${Math.max(0, autoLoadMoreThreshold)}px 0px`,
      }
    );

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
    };
  }, [
    autoLoadMoreThreshold,
    enableAutoLoadMore,
    hasMoreConversations,
    loading,
    loadingMoreConversations,
    onLoadMoreConversations,
    conversations.length,
  ]);

  return (
    <aside className={cn('min-h-0 overflow-hidden bg-muted/20', className)}>
      <div className="flex flex-col gap-1 px-2 pt-2.5 pb-2">
        <div
          role="group"
          aria-label="Primary chat actions"
          className="flex items-center gap-1"
        >
          <div className="min-w-0 flex-1">{newChatAction}</div>
          {sidebarToggleAction}
        </div>

        <ChatSidebarSearchEntry
          isActive={isSearchOpen}
          onClick={onSearchOpen}
        />
        <ChatSidebarNavItem
          icon={<Box {...chatLucideIconProps} className="size-4" />}
          label="Books"
          onClick={onOpenBook}
        />
      </div>

      <div className="mx-2 border-t border-border/40" />

      <div
        ref={conversationScrollAreaRef}
        aria-label="Conversation history"
        className="flex min-h-0 flex-1 flex-col overflow-y-auto px-2 py-1.5"
      >
        {loading ? (
          <div className="chat-type-body-sm rounded-md px-3 py-4 text-muted-foreground">
            Loading conversations…
          </div>
        ) : conversations.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center px-3 py-8 text-center">
            <p className="chat-type-body-sm text-muted-foreground">
              {hasMoreConversations
                ? loadingMoreConversations
                  ? 'Loading older conversations…'
                  : 'Older conversations will load automatically as you scroll.'
                : 'No conversations yet.'}
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {conversationGroups.map(group => (
              <section key={group.id} className="flex flex-col gap-1">
                <div className="chat-type-caption px-3 py-1 text-muted-foreground/80">
                  {group.label}
                </div>

                <div className="flex flex-col gap-px">
                  {group.conversations.map(conversation => {
                    const groupConversationId =
                      normalizedConversationId(conversation);
                    if (!groupConversationId) {
                      return null;
                    }
                    const isActive =
                      groupConversationId === activeConversationId;
                    const isDeleting =
                      deletingConversationId === groupConversationId;
                    const isRenaming =
                      renamingConversationId === groupConversationId;
                    return (
                      <ChatSidebarConversationItem
                        key={groupConversationId}
                        conversation={conversation}
                        isActive={isActive}
                        isDeleting={isDeleting}
                        isRenaming={isRenaming}
                        onSelect={onSelect}
                        onRename={onRename}
                        onDelete={onDelete}
                      />
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
        )}

        {!loading && hasMoreConversations ? (
          <div className="px-2 pt-1" aria-live="polite">
            <div
              ref={conversationLoadMoreSentinelRef}
              aria-hidden="true"
              className="h-px w-full shrink-0"
            />
            <div className="ml-8 flex min-h-6 items-center text-xs text-muted-foreground">
              {loadingMoreConversations ? (
                <>
                  <Loader2
                    {...chatLucideIconProps}
                    className="mr-2 size-4 animate-spin"
                  />
                  Loading older conversations…
                </>
              ) : (
                'Scroll to load older conversations'
              )}
            </div>
          </div>
        ) : null}
      </div>

      {currentUser ? (
        <ChatSidebarAccount
          user={currentUser}
          credits={credits}
          loadingCredits={loadingCredits}
          onLogout={onLogout}
          onOpenBilling={onOpenBilling}
          onOpenRedeem={onOpenRedeem}
          onOpenComputers={onOpenComputers}
          onOpenBook={onOpenBook}
          onOpenIntegrations={onOpenIntegrations}
          onDownloadClient={onDownloadClient}
        />
      ) : null}
    </aside>
  );
}
